import { useMemo } from "react";

import { CampaignValidationError, validateCampaignConfig } from "@signalist/shared-types";

import { useGetSendersUsersQuery } from "@/pages/Private/redux/admin/admin.api";

import { CopywritingCampaign } from "@/pages/Private/pages/List/pages/ListEditCopywriting/ListEditCopywriting";
import { getSuggestions } from "@/pages/Private/pages/List/utils/column-utils";
import { List } from "@/pages/Private/pages/List/schema/list";

export interface CampaignValidationState {
	/**
	 * The **persisted** flag. It is what the API enforces on activation and what decides whether a
	 * campaign can be picked, so the badge, the picker and the server never disagree about *whether*.
	 */
	isValid: boolean;
	/** Live reasons, recomputed from the stored config — the *why*, which used to exist only in the builder. */
	errors: CampaignValidationError[];
	/**
	 * The stored flag says invalid but the rules find nothing wrong: a stale snapshot, because the
	 * flag is only recomputed while its campaign is open in the builder. 235 campaigns are flagged
	 * invalid in production and some of them are of this kind, so the UI has to offer the one action
	 * that clears it (open the campaign) instead of listing reasons it cannot produce.
	 */
	needsRefresh: boolean;
}

/**
 * Per-campaign validity for a whole list (PREC-3952), keyed by campaign uuid.
 *
 * Reasons are derived live rather than read from storage: only the boolean is persisted, and the
 * sentences behind it were trapped in the campaign builder's component state.
 */
export const useCampaignValidation = (list?: List): Record<string, CampaignValidationState> => {
	const { data: sendingUsers } = useGetSendersUsersQuery();

	return useMemo(() => {
		const config = (list?.campaignConfig ?? {}) as Record<string, CopywritingCampaign | null>;
		const validKeys = getSuggestions(undefined, true, list?.columns, list?.event);
		const senderEmails = sendingUsers?.data?.flatMap((user) => user.emails ?? []) ?? [];

		const states: Record<string, CampaignValidationState> = {};

		for (const [id, campaign] of Object.entries(config)) {
			// `name` is list metadata on campaignConfig, not a campaign.
			if (id === "name" || !campaign) {
				continue;
			}

			const { isValid: rulesPass, errors } = validateCampaignConfig({
				campaign,
				nodes: campaign.initialNodes ?? [],
				edges: campaign.initialEdges ?? [],
				validKeys,
				senderEmails,
			});
			const storedValid = Boolean(campaign.isValid);

			states[id] = { isValid: storedValid, errors, needsRefresh: !storedValid && rulesPass };
		}

		return states;
	}, [list, sendingUsers]);
};
